import { useEffect, useState } from "react";
import {
  buildApiUrl,
  hasUserSessionHint,
  invalidateCache,
  subscribeToUserSession,
} from "../services/api";
import { getDeviceFingerprint, getDeviceId } from "../utils/deviceId";

const STREAM_EVENT = "cache-invalidate";
const SUPPORT_MESSAGE_EVENT = "support-message";
const SUPPORT_ASSIGNED_EVENT = "support-assigned";
const BILLING_UPDATE_EVENT = "billing-update";
const SUPPORT_MESSAGE_CUSTOM_EVENT = "aethercv-support-message";
const SUPPORT_ASSIGNED_CUSTOM_EVENT = "aethercv-support-assigned";
const BILLING_UPDATE_CUSTOM_EVENT = "aethercv-billing-update";

export default function ServerEvents() {
  const [hasSession, setHasSession] = useState(() => hasUserSessionHint());

  useEffect(() => subscribeToUserSession(({ hasSession: nextHasSession }) => {
    setHasSession(Boolean(nextHasSession));
  }), []);

  useEffect(() => {
    if (!hasSession) return undefined;

    const streamUrl = new URL(buildApiUrl("/user/stream"), window.location.origin);
    const deviceId = getDeviceId();
    const deviceFingerprint = getDeviceFingerprint();
    if (deviceId) {
      streamUrl.searchParams.set("deviceId", deviceId);
    }
    if (deviceFingerprint) {
      streamUrl.searchParams.set("deviceFingerprint", deviceFingerprint);
    }
    const source = new EventSource(streamUrl.toString(), { withCredentials: true });

    const handleInvalidate = (event) => {
      if (!event?.data) return;
      try {
        const payload = JSON.parse(event.data);
        if (payload?.prefixes) {
          invalidateCache(payload.prefixes);
        }
      } catch {
        // Ignore malformed payloads.
      }
    };

    const dispatchEvent = (eventName) => (event) => {
      if (!event?.data) return;
      try {
        const payload = JSON.parse(event.data);
        window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
      } catch {
        // Ignore malformed payloads.
      }
    };

    const handleSupportMessage = dispatchEvent(SUPPORT_MESSAGE_CUSTOM_EVENT);
    const handleSupportAssigned = dispatchEvent(SUPPORT_ASSIGNED_CUSTOM_EVENT);
    const handleBillingUpdate = dispatchEvent(BILLING_UPDATE_CUSTOM_EVENT);

    source.addEventListener(STREAM_EVENT, handleInvalidate);
    source.addEventListener(SUPPORT_MESSAGE_EVENT, handleSupportMessage);
    source.addEventListener(SUPPORT_ASSIGNED_EVENT, handleSupportAssigned);
    source.addEventListener(BILLING_UPDATE_EVENT, handleBillingUpdate);
    source.onerror = () => {
      // EventSource reconnects automatically.
    };

    return () => {
      source.removeEventListener(STREAM_EVENT, handleInvalidate);
      source.removeEventListener(SUPPORT_MESSAGE_EVENT, handleSupportMessage);
      source.removeEventListener(SUPPORT_ASSIGNED_EVENT, handleSupportAssigned);
      source.removeEventListener(BILLING_UPDATE_EVENT, handleBillingUpdate);
      source.close();
    };
  }, [hasSession]);

  return null;
}
